/**
 * Deck, shuffling and dealing. Only the live game needs these — the
 * scorekeeper mode uses physical cards — but they live in shared so the rules
 * stay in one place.
 */

import type { Card, Rank, Suit, TrumpSuit } from '@shared/types/game';
import { RANKS, SUITS } from '@shared/types/game';
import { DECK_SIZE, assertPlayerCount } from './rounds';

/** Injectable so tests can deal a known hand. Must return [0, 1). */
export type Rng = () => number;

export function createDeck(): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ suit, rank });
    }
  }
  return deck;
}

/** Fisher-Yates. Returns a new array; the input is not mutated. */
export function shuffle<T>(items: readonly T[], rng: Rng = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export interface DealResult {
  /** Indexed by seat. */
  hands: Card[][];
  /** The turned-up card that sets trump, or null when the deck ran out. */
  trumpCard: Card | null;
  /** Null means the round is played troefloos. */
  trumpSuit: TrumpSuit;
  /** Cards left over, face down and unused this round. */
  remaining: Card[];
}

/**
 * Deal `cards` to each seat, then turn the next card for trump.
 *
 * In the biggest round the deck can be exhausted exactly (13 cards × 4
 * players), leaving no card to turn — that round is played without trump.
 *
 * Cards are handed out in blocks rather than one at a time around the table;
 * from a shuffled deck the resulting distribution is equivalent.
 */
/**
 * Ranks in the short deck: seven up to ace, thirty-two cards.
 *
 * Klaverjassen builds its own from the same list rather than calling this, and
 * that stays as it is on purpose: its tests deal from a seeded rng, so a change
 * in the order the deck is built changes every expected hand in the file.
 */
export const SHORT_RANKS: readonly Rank[] = [7, 8, 9, 10, 11, 12, 13, 14];

export function createShortDeck(): Card[] {
  return SUITS.flatMap((suit) => SHORT_RANKS.map((rank) => ({ suit, rank })));
}

/** Deal from a deck you choose, with nothing turned for trump. */
export function dealFrom(
  deck: readonly Card[],
  playerCount: number,
  cards: number,
  rng: Rng = Math.random
): Card[][] {
  if (cards * playerCount > deck.length) {
    throw new RangeError(
      `cannot deal ${cards} cards to ${playerCount} players from a ${deck.length}-card deck`
    );
  }
  const shuffled = shuffle(deck, rng);
  return Array.from({ length: playerCount }, (_, seat) =>
    shuffled.slice(seat * cards, (seat + 1) * cards)
  );
}

export function deal(playerCount: number, cards: number, rng: Rng = Math.random): DealResult {
  assertPlayerCount(playerCount);
  if (!Number.isInteger(cards) || cards < 1) {
    throw new RangeError(`cards must be a positive integer, got ${cards}`);
  }
  if (cards * playerCount > DECK_SIZE) {
    throw new RangeError(
      `cannot deal ${cards} cards to ${playerCount} players from a ${DECK_SIZE}-card deck`
    );
  }

  const deck = shuffle(createDeck(), rng);
  const hands: Card[][] = [];
  for (let seat = 0; seat < playerCount; seat++) {
    hands.push(deck.slice(seat * cards, (seat + 1) * cards));
  }

  const rest = deck.slice(playerCount * cards);
  const trumpCard = rest.length > 0 ? rest[0] : null;

  return {
    hands,
    trumpCard,
    trumpSuit: trumpCard ? trumpCard.suit : null,
    remaining: rest.slice(1),
  };
}

export function cardId(card: Card): string {
  return `${card.suit}-${card.rank}`;
}

export function cardsEqual(a: Card, b: Card): boolean {
  return a.suit === b.suit && a.rank === b.rank;
}

export function hasCard(hand: readonly Card[], card: Card): boolean {
  return hand.some((held) => cardsEqual(held, card));
}

export function removeCard(hand: readonly Card[], card: Card): Card[] {
  const index = hand.findIndex((held) => cardsEqual(held, card));
  if (index === -1) {
    throw new Error(`card ${cardId(card)} is not in hand`);
  }
  return [...hand.slice(0, index), ...hand.slice(index + 1)];
}

/**
 * Hand sorted for display: trump group first, then the other suits in a fixed
 * order, each suit high to low.
 */
export function sortHand(hand: readonly Card[], trumpSuit: TrumpSuit): Card[] {
  const order = new Map<Suit, number>();
  const suitOrder: Suit[] = trumpSuit
    ? [trumpSuit, ...SUITS.filter((suit) => suit !== trumpSuit)]
    : [...SUITS];
  suitOrder.forEach((suit, index) => order.set(suit, index));

  return [...hand].sort(
    (a, b) => (order.get(a.suit) ?? 0) - (order.get(b.suit) ?? 0) || b.rank - a.rank
  );
}

/** Rank shown on the card face. */
export function rankLabel(rank: Rank): string {
  switch (rank) {
    case 14:
      return 'A';
    case 13:
      return 'K';
    case 12:
      return 'Q';
    case 11:
      return 'J';
    default:
      return String(rank);
  }
}

export const SUIT_SYMBOLS: Record<Suit, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

export function isRedSuit(suit: Suit): boolean {
  return suit === 'hearts' || suit === 'diamonds';
}
